import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import { type CountryData, type CountryIso2, defaultCountries, parseCountry } from 'react-international-phone';

import { normalizeAllowedPhonePrefixes } from '@/lib/phone-prefix';
import { getCountryCodeForSite } from '@/lib/site-country';

/** The library ships English country names: translate them to the page language (WCAG 3.1.2). */
export function localizeCountryNames(countries: CountryData[], language: string): CountryData[] {
    let displayNames: Intl.DisplayNames;
    try {
        displayNames = new Intl.DisplayNames([language], { type: 'region' });
    } catch {
        return countries;
    }
    return countries.map((country) => {
        const [name, iso2, ...rest] = country;
        return [displayNames.of(iso2.toUpperCase()) ?? name, iso2, ...rest] as CountryData;
    });
}

export type PhoneInputConfig = {
    countries: CountryData[];
    defaultCountry: CountryIso2;
    allowedPhonePrefixes: string[];
};

/**
 * Adapts the phone-input catalog to an explicit prefix list.
 * An omitted or empty list leaves every country selectable (address and the other free-prefix forms).
 * Match is equality on the dial code: `+39` does not include another code that merely starts with 39.
 */
export function useAllowedPhoneCountries(allowedPrefixes?: string | readonly string[]): PhoneInputConfig {
    const { site } = useSite();
    const { i18n } = useTranslation();
    const language = i18n.language;
    const prefixKey = normalizeAllowedPhonePrefixes(allowedPrefixes).join('\u0000');

    return useMemo(() => {
        const prefixes = prefixKey ? prefixKey.split('\u0000').filter(Boolean) : [];
        const countries = localizeCountryNames(
            prefixes.length === 0
                ? defaultCountries
                : defaultCountries.filter((country) => prefixes.includes(`+${parseCountry(country).dialCode}`)),
            language
        );
        const preferred = getCountryCodeForSite(site.id).toLowerCase() as CountryIso2;
        const preferredAllowed = countries.some((country) => parseCountry(country).iso2 === preferred);
        return {
            countries,
            defaultCountry: preferredAllowed || countries.length === 0 ? preferred : parseCountry(countries[0]).iso2,
            allowedPhonePrefixes: prefixes,
        };
    }, [site.id, prefixKey, language]);
}

/** Unfiltered phone input. Registration and profile pass `useAllowedPhoneCountries` a prefix list instead. */
export function usePhoneInputConfig(): PhoneInputConfig {
    return useAllowedPhoneCountries();
}
